import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useCart } from "@/hooks/useCart";
import { useStoreSettings } from "@/hooks/useStoreSettings";

export interface WhatsAppConfig {
  phone: string;
  attendantName: string;
  defaultMessage: string;
  openHour: number; // 0-23
  closeHour: number; // 0-23
}

export type PageContext = "product" | "cart" | "home" | "other";

interface ProductContext {
  name: string;
  price: string;
  url: string;
}

export function useWhatsAppConfig(): WhatsAppConfig {
  const { settings } = useStoreSettings();
  const sd = settings.settings_data;

  return useMemo(() => ({
    phone: sd.whatsapp_phone || sd.notif_whatsapp_numero || settings.phone || sd.phone || "",
    attendantName: sd.whatsapp_attendant || "Atendimento",
    defaultMessage: sd.whatsapp_default_message || "Olá! Preciso de ajuda.",
    openHour: Number(sd.whatsapp_open_hour) || 8,
    closeHour: Number(sd.whatsapp_close_hour) || 18,
  }), [sd, settings.phone]);
}

export function useWhatsAppStatus(config: WhatsAppConfig) {
  return useMemo(() => {
    const now = new Date();
    const hour = now.getHours();
    if (hour >= config.openHour && hour < config.closeHour) return "online" as const;
    if (hour >= config.closeHour - 1 && hour < config.closeHour) return "closing" as const;
    return "offline" as const;
  }, [config.openHour, config.closeHour]);
}

export function usePageContext(): { context: PageContext; product: ProductContext | null } {
  const location = useLocation();

  return useMemo(() => {
    const path = location.pathname;
    if (path.includes("/product/") || path.includes("/produto/")) {
      return { context: "product" as const, product: null }; // product data injected externally
    }
    if (path.includes("/cart") || path.includes("/carrinho")) {
      return { context: "cart" as const, product: null };
    }
    if (path === "/" || path.match(/\/loja\/[^/]+\/?$/)) {
      return { context: "home" as const, product: null };
    }
    return { context: "other" as const, product: null };
  }, [location.pathname]);
}

export function buildWhatsAppUrl(phone: string, message: string, source?: string): string {
  const cleanPhone = phone.replace(/\D/g, "");
  if (!cleanPhone) return "#";
  const sourceTag = source ? `\n\n[via: ${source}]` : "";
  return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message + sourceTag)}`;
}

export function buildCartMessage(items: Array<{ name: string; quantity: number; price: number }>, total: number): string {
  const itemsList = items.map(i => `• ${i.name} (x${i.quantity}) — R$ ${i.price.toFixed(2)}`).join("\n");
  return `Olá! Preciso de ajuda com meu carrinho:\n\n${itemsList}\n\n💰 Total: R$ ${total.toFixed(2)}`;
}

export function buildProductMessage(name: string, price: string, url: string): string {
  return `Olá! Tenho interesse neste produto:\n\n🛍 Produto: ${name}\n💰 Preço: ${price}\n🔗 ${url}`;
}
